import * as Monaco from 'monaco-editor/esm/vs/editor/editor.api';

/**
 * Simple global registry that maps document URIs to their Monaco editor instances.
 * This allows cross-component communication (e.g. highlighting query text from the
 * profiler result view) without threading editor refs through the component tree.
 */
const editors = new Map<string, Monaco.editor.IStandaloneCodeEditor>();

export const registerEditor = (
  documentId: string,
  editor: Monaco.editor.IStandaloneCodeEditor
) => {
  editors.set(documentId, editor);
};

export const unregisterEditor = (documentId: string) => {
  editors.delete(documentId);
};

export const getEditorForDocument = (
  documentId: string
): Monaco.editor.IStandaloneCodeEditor | undefined => {
  return editors.get(documentId);
};

/** Key used to register the explain/profile result editor for a given note. */
export const getExplainEditorKey = (noteId: string) => `${noteId}-explain`;

export const QUERY_HIGHLIGHT_CLASS = 'sd-profiler-query-highlight';
export const PLAN_HIGHLIGHT_CLASS = 'sd-profiler-plan-highlight';

// ── Shared profiler highlight state ─────────────────────────────────────
// Both the query editor and the explain result editor read/write these so
// that highlights stay in sync across the two panes.

interface ProfilerHighlightState {
  queryDecorations: string[];
  planDecorations: string[];
}

const highlightState = new Map<string, ProfilerHighlightState>();

const getHighlightState = (noteId: string): ProfilerHighlightState => {
  let state = highlightState.get(noteId);
  if (!state) {
    state = { queryDecorations: [], planDecorations: [] };
    highlightState.set(noteId, state);
  }
  return state;
};

/**
 * Clears all profiler highlights from both editors and removes the state entry.
 */
export const removeProfilerHighlights = (noteId: string) => {
  setProfilerHighlights(noteId, null, null);
  highlightState.delete(noteId);
};

/**
 * Sets synced highlights in both the query editor and the explain editor.
 * Clears any previous highlights first. Either parameter may be null to
 * only highlight in one editor.
 */
export const setProfilerHighlights = (
  noteId: string,
  queryHighlights: Monaco.editor.IModelDeltaDecoration[] | null,
  planHighlights: Monaco.editor.IModelDeltaDecoration[] | null
) => {
  const state = getHighlightState(noteId);
  const queryEditor = getEditorForDocument(noteId);
  const explainEditor = getEditorForDocument(getExplainEditorKey(noteId));

  if (queryEditor) {
    state.queryDecorations = queryEditor.deltaDecorations(
      state.queryDecorations,
      queryHighlights || []
    );
  }

  if (explainEditor) {
    state.planDecorations = explainEditor.deltaDecorations(
      state.planDecorations,
      planHighlights || []
    );
  }
};
